//Global imports
import { CRow, CButton, CInputGroup, CFormInput } from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { cilFolder } from '@coreui/icons';
import CIcon from '@coreui/icons-react';

//Component imports
import ArticleCard from '../ArticleCard';
import Loader from '../../../../components/Layout/Loader';

//File imports
import Notification from '../../../../services/Notification/Notification';
import Logger from '../../../../services/Logger/Logger';
import { Language } from '../../../../language/English';
import { ArticlePojo } from '../../Api/ArticlePojo';
import { ARTICLE_STATUS_DELETED } from '../../Api/ArticleConstants';
import { ArticleService } from '../../Api/ArticleService';
import CreateArticleModal from '../CreateArticleModal';
import useInitialLoad from '../../../../services/Hooks/useInitialLoad';

const ArticleList: React.FC = () => {
    const notification = new Notification();
    const logger = new Logger();
    const articleService = new ArticleService();

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [editingTemplate, setEditingTemplate] = useState<ArticlePojo | null>(
        null,
    );
    const [searchText, setSearchText] = useState<string>('');
    const [articles, setArticles] = useState<ArticlePojo[][] | null>(null);

    useInitialLoad({
        loaderCallback: () => {
            setShowLoader(true);
            fetchAllArticles();
        },
    });

    const handleEdit = (template: ArticlePojo) => {
        setEditingTemplate(template);
        setShowCreateModal(true);
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setEditingTemplate(null);
    };

    const handleSuccess = () => {
        fetchAllArticles();
        handleModalClose();
    };

    const fetchAllArticles = async () => {
        try {
            const response = await articleService.getAll();
            if (response && response.isSuccess) {
                const chunkedArticles: any = [];
                for (let i = 0; i < response?.articles.length; i += 2) {
                    chunkedArticles.push(response?.articles.slice(i, i + 2));
                }
                setArticles(chunkedArticles);
            } else {
                notification.error(Language.CANNOT_LOAD_ARTICLE_ERROR);
            }
            setShowLoader(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const _changeArticleStatus = async (articleId: number, status: number) => {
        setShowLoader(true);
        try {
            const articleService = new ArticleService();
            let response = await articleService.delete(articleId, status);
            if (response) {
                if (status === ARTICLE_STATUS_DELETED) {
                    notification.success(Language.DELETED);
                } else {
                    notification.success(Language.UPDATED);
                }
                fetchAllArticles();
            } else {
                notification.error(Language.CANNOT_CHANGE_ARTICLE_STATUS_ERROR);
                setShowLoader(false);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const filteredArticles = articles
        ?.map((row: ArticlePojo[]) =>
            row.filter((article: ArticlePojo) =>
                article.name.toLowerCase().includes(searchText.toLowerCase()),
            ),
        )
        .filter((row) => row.length > 0);

    return (
        <>
            {showLoader ? <Loader /> : null}

            <div className="d-flex justify-content-between align-items-center mb-4 search-box-container">
                {articles && articles?.length > 0 ? (
                    <CInputGroup className="w-50 search-box">
                        <CFormInput
                            placeholder={Language.SEARCH_ARTICLES}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </CInputGroup>
                ) : (
                    <div></div>
                )}

                <CButton
                    color="primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    {Language.CREATE_ARTICLE}
                </CButton>
            </div>

            {filteredArticles && filteredArticles.length > 0 ? (
                <CRow>
                    {filteredArticles.map((row: any) =>
                        row.map((article: ArticlePojo) => (
                            <ArticleCard
                                key={article.articleId}
                                article={article}
                                deleteArticle={() =>
                                    _changeArticleStatus(
                                        article.articleId,
                                        ARTICLE_STATUS_DELETED,
                                    )
                                }
                                changeArticleStatus={(status: number) =>
                                    _changeArticleStatus(
                                        article.articleId,
                                        status,
                                    )
                                }
                                onEdit={handleEdit}
                            />
                        )),
                    )}
                </CRow>
            ) : articles && Array.isArray(articles) && articles.length === 0 ? (
                <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: '60vh' }}
                >
                    <CIcon
                        icon={cilFolder}
                        size="3xl"
                        className="mb-3 text-secondary"
                    />
                    <h4 className="mb-3 text-center text-secondary fs-6">
                        {Language.NO_ARTICLE} <br />
                        {Language.CREATE_HERE}
                    </h4>
                    <CButton
                        color="primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        {Language.CREATE_ARTICLE}
                    </CButton>
                </div>
            ) : null}

            {showCreateModal && (
                <CreateArticleModal
                    visible={showCreateModal}
                    onClose={handleModalClose}
                    onSuccess={handleSuccess}
                    mode={editingTemplate ? 'edit' : 'create'}
                    articleData={editingTemplate}
                />
            )}
        </>
    );
};

export default ArticleList;
